<template>
  <div class="rd__overlay" @click.self="close" @keydown.esc="close">
    <div class="rd" role="dialog" aria-modal="true" :aria-label="dialogTitle" ref="panel" tabindex="-1">
      <button class="rd__close" type="button" aria-label="Fermer" @click="close">✕</button>

      <h2 class="rd__title">{{ dialogTitle }}</h2>

      <template v-if="!isAuth">
        <p class="rd__hint">Tu dois être connecté pour signaler.</p>
        <div class="rd__actions">
          <BananaButton variant="dark" size="sm" :to="loginTo">Se connecter</BananaButton>
          <BananaButton variant="ghost" size="sm" @click="close">Annuler</BananaButton>
        </div>
      </template>

      <template v-else-if="done">
        <p class="rd__ok">✅ Merci ! Ton signalement a bien été envoyé aux modérateurs.</p>
        <div class="rd__actions">
          <BananaButton variant="dark" size="sm" @click="close">Fermer</BananaButton>
        </div>
      </template>

      <form v-else class="rd__form" @submit.prevent="submit">
        <label class="rd__label" :for="msgId">
          {{ target === 'joke' ? 'Pourquoi signaler cette blague ?' : 'Pourquoi signaler ce commentaire ?' }}
        </label>
        <textarea
          :id="msgId"
          v-model="message"
          class="rd__textarea"
          rows="3"
          placeholder="Explique le problème (contenu offensant, hors-sujet…)"
          required
        ></textarea>

        <template v-if="target === 'joke'">
          <label class="rd__label" :for="rewriteId">Proposer une réécriture (optionnel)</label>
          <textarea
            :id="rewriteId"
            v-model="proposedRewrite"
            class="rd__textarea"
            rows="3"
            placeholder="Une version plus drôle / plus correcte, si tu veux…"
          ></textarea>
        </template>

        <p v-if="error" class="rd__error">{{ error }}</p>

        <div class="rd__actions">
          <BananaButton type="submit" variant="danger" size="sm" :loading="sending" :disabled="!canSubmit">
            Envoyer le signalement
          </BananaButton>
          <BananaButton type="button" variant="ghost" size="sm" @click="close">Annuler</BananaButton>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'
import { REPORT_JOKE_MUTATION, REPORT_COMMENT_MUTATION } from '@/graphql/moderation'
import { isAuthenticated } from '@/auth'

let uid = 0

export default {
  name: 'ReportDialog',
  components: { BananaButton },
  props: {
    target: { type: String, required: true }, // 'joke' | 'comment'
    targetId: { type: Number, required: true },
  },
  emits: ['close', 'reported'],
  data() {
    uid += 1
    return {
      message: '',
      proposedRewrite: '',
      sending: false,
      error: '',
      done: false,
      uid,
    }
  },
  computed: {
    isAuth() {
      return isAuthenticated()
    },
    dialogTitle() {
      return this.target === 'joke' ? 'Signaler cette blague' : 'Signaler ce commentaire'
    },
    msgId() {
      return `rd-msg-${this.uid}`
    },
    rewriteId() {
      return `rd-rw-${this.uid}`
    },
    canSubmit() {
      return this.message.trim().length > 0 && !this.sending
    },
    loginTo() {
      return { path: '/login', query: { redirect: this.$route.fullPath } }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    this.$nextTick(() => {
      if (this.$refs.panel) this.$refs.panel.focus()
    })
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.close()
    },
    close() {
      this.$emit('close')
    },
    async submit() {
      if (!this.canSubmit) return
      this.error = ''
      this.sending = true
      try {
        if (this.target === 'joke') {
          await this.$apollo.mutate({
            mutation: REPORT_JOKE_MUTATION,
            variables: {
              jokeId: this.targetId,
              message: this.message.trim(),
              proposedRewrite: this.proposedRewrite.trim() || null,
            },
          })
        } else {
          await this.$apollo.mutate({
            mutation: REPORT_COMMENT_MUTATION,
            variables: {
              commentId: this.targetId,
              message: this.message.trim(),
            },
          })
        }
        this.done = true
        this.$emit('reported')
      } catch (e) {
        const raw = (e && e.message) ? e.message : ''
        if (/déjà|already|signal/i.test(raw)) {
          this.error = 'Tu as déjà signalé ce contenu. Merci, les modérateurs sont sur le coup !'
        } else {
          this.error = raw.replace(/^GraphQL error:\s*/i, '') || 'Une erreur est survenue. Réessaie plus tard.'
        }
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.rd__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(26, 18, 7, 0.55);
  backdrop-filter: blur(2px);
  animation: rd-fade 0.2s var(--ease-out);
}
.rd {
  position: relative;
  width: 100%;
  max-width: 460px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-pop);
  padding: 26px 24px 22px;
  animation: rd-pop 0.25s var(--ease-bounce);
}
.rd:focus { outline: none; }
.rd__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: var(--border-w) solid var(--border-c);
  border-radius: 50%;
  background: var(--banana-500);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--ink-900);
  transition: transform 0.15s var(--ease-bounce);
}
.rd__close:hover { transform: rotate(90deg) scale(1.08); }
.rd__title {
  font-size: 1.35rem;
  margin: 0 40px 14px 0;
}
.rd__form { display: flex; flex-direction: column; gap: 6px; }
.rd__label {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  margin-top: 8px;
  color: var(--ink-800);
}
.rd__textarea {
  width: 100%;
  font-family: var(--font-body);
  font-size: 0.98rem;
  padding: 10px 12px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-sm);
  background: var(--cream);
  color: var(--ink-900);
  resize: vertical;
}
.rd__textarea:focus { outline: 3px solid var(--banana-500); outline-offset: 1px; }
.rd__hint { color: var(--text-soft); margin: 6px 0 16px; }
.rd__ok { color: var(--peel-green-dark); font-weight: 600; margin: 6px 0 18px; }
.rd__error {
  color: var(--bruise);
  font-weight: 600;
  font-size: 0.9rem;
  margin: 8px 0 0;
}
.rd__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
}

@keyframes rd-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes rd-pop { from { opacity: 0; transform: translateY(14px) scale(0.97); } to { opacity: 1; transform: none; } }

@media (max-width: 480px) {
  .rd { padding: 22px 18px 18px; border-radius: var(--r-md); }
}
</style>
